import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { formatNum } from "../DataTable";

ChartJS.register(
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const topCustomerStyles = {
  card: {
    boxSizing: "border-box",
    fontFamily: 'Segoe UI',
    background: "#FFFFFF",
    border: "1px solid #E3E3E3",
    borderRadius: 8,
    padding: "24px 16px",
    height: "100%",
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    paddingBottom: 24,
    marginBottom: 0,
  },
  dragHandle: {
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: 4,
    lineHeight: 0,
    background: "transparent",
    border: "none",
    padding: 0,
  },
  titleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
    flexWrap: "wrap",
  },
  headWord: {
    fontFamily: 'Segoe UI',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "24px",
    color: "#606060",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
    marginLeft: "auto",
  },
  tableWrap: {
    width: "100%",
    overflow: "auto",
    maxHeight: 420,
  },
  dataTable: {
    width: "100%",
    maxWidth: '100%',
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontFamily: 'Segoe UI',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "24px",
  },
  thIndex: {
    boxSizing: "border-box",
    width: 40,
    height: 44,
    padding: 8,
    textAlign: "center",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "28px",
    color: "#606060",
    borderTop: "1px solid #E3E3E3",
    borderBottom: "2px solid #E3E3E3",
  },
  thSort: {
    boxSizing: "border-box",
    height: 44,
    padding: 8,
    verticalAlign: "middle",
    borderTop: "1px solid #E3E3E3",
    borderBottom: "2px solid #E3E3E3",
  },
  thSortInner: {
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    maxWidth: "100%",
  },
  thLabel: {
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "28px",
    color: "#606060",
  },
  tdIndex: {
    boxSizing: "border-box",
    padding: 8,
    height: 44,
    textAlign: "center",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "24px",
    color: "#606060",
    borderBottom: "1px solid #E3E3E3",
  },
  tdName: {
    boxSizing: "border-box",
    padding: 8,
    height: 44,
    textAlign: "left",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "24px",
    color: "#606060",
    borderBottom: "1px solid #E3E3E3",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tdNumCenter: {
    boxSizing: "border-box",
    padding: 8,
    height: 44,
    textAlign: "center",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "24px",
    color: "#606060",
    borderBottom: "1px solid #E3E3E3",
  },
  tdNumRight: {
    boxSizing: "border-box",
    padding: 8,
    height: 44,
    textAlign: "right",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "24px",
    color: "#606060",
    borderBottom: "1px solid #E3E3E3",
  },
  topNBtn: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#FFFFFF",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: 'Segoe UI',
    lineHeight: "normal",
    outline: "none",
  },
  byTypeBtn: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#FFFFFF",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: 'Segoe UI',
    lineHeight: "normal",
    outline: "none",
  },
  topNChevron: { fontSize: 14, color: "#606060" },
  byTypeChevron: { fontSize: 14, color: "#606060" },
  dots: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#606060",
    fontSize: 22,
    lineHeight: 1,
    padding: 0,
    userSelect: "none",
    outline: "none",
  },
};

function rowMetric(row, byAmount) {
  return byAmount
    ? Number(row.salesAmount) || 0
    : Number(row.qty) || 0;
}

function getAgeBandOrder(ageValue) {
  const s = String(ageValue ?? "").trim().toLowerCase();
  if (!s) return Number.MAX_SAFE_INTEGER;
  if (s.startsWith(">")) {
    const n = Number(s.replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n + 1000 : Number.MAX_SAFE_INTEGER;
  }
  const rangeStart = s.match(/^(\d+(?:\.\d+)?)/);
  if (rangeStart) return Number(rangeStart[1]);
  const anyNum = s.match(/(\d+(?:\.\d+)?)/);
  if (anyNum) return Number(anyNum[1]);
  return Number.MAX_SAFE_INTEGER;
}

function sortAgeRowsAsc(rows = []) {
  return [...rows].sort((a, b) => {
    const oa = getAgeBandOrder(a.age);
    const ob = getAgeBandOrder(b.age);
    if (oa !== ob) return oa - ob;
    return String(a.age ?? "").localeCompare(String(b.age ?? ""));
  });
}

function TopNDropdown({ value, onChange, variant }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const choices = [10, 20, 30].map((n) => ({
    key: String(n),
    label: `Top ${n}`,
  }));
  const selected = choices.find((c) => c.key === value) || choices[0];
  const btnStyle =
    variant === "topCustomer" ? topCustomerStyles.topNBtn : styles.topNBtn;
  const chevStyle =
    variant === "topCustomer"
      ? topCustomerStyles.topNChevron
      : styles.topNChevron;
  return (
    <div style={styles.topNWrapper} ref={ref}>
      <button style={btnStyle} onClick={() => setOpen(!open)} type="button">
        {selected.label}
        <span style={chevStyle}>▾</span>
      </button>
      {open && (
        <div style={styles.topNMenu}>
          {choices.map((c) => (
            <button
              key={c.key}
              style={{
                ...styles.topNOption,
                background: value === c.key ? "#eef2f3" : "#fff",
                fontWeight: value === c.key ? 600 : 400,
              }}
              onClick={() => {
                onChange(c.key);
                setOpen(false);
              }}
              type="button"
            >
              {c.label}
              {value === c.key && (
                <span style={{ color: "#5CC7C1", marginLeft: "auto" }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ByAmountsDropdown({ value, onChange, variant }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const items = [
    { key: "Amount", label: "By Amount" },
    { key: "Qty", label: "By Qty" },
  ];
  const selected = items.find((i) => i.key === value) || items[0];
  const btnStyle =
    variant === "topCustomer"
      ? topCustomerStyles.byTypeBtn
      : styles.byTypeBtn;
  const chevStyle =
    variant === "topCustomer"
      ? topCustomerStyles.byTypeChevron
      : styles.byTypeChevron;
  return (
    <div style={styles.byTypeWrapper} ref={ref}>
      <button style={btnStyle} onClick={() => setOpen(!open)} type="button">
        {selected.label} <span style={chevStyle}>▾</span>
      </button>
      {open && (
        <div style={styles.byTypeMenu}>
          {items.map((item) => (
            <button
              key={item.key}
              style={{
                ...styles.byTypeOption,
                background: value === item.key ? "#eef2f3" : "#fff",
                fontWeight: value === item.key ? 600 : 400,
              }}
              onClick={() => {
                onChange(item.key);
                setOpen(false);
              }}
              type="button"
            >
              {item.label}
              {value === item.key && (
                <span style={{ color: "#5CC7C1", marginLeft: "auto" }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const handlePrint = () => {
    window.print();
    setOpen(false);
  };
  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.png";
    link.click();
    setOpen(false);
  };
  return (
    <div style={styles.menuWrapper} ref={ref}>
      <button
        style={styles.dots}
        onClick={() => setOpen(!open)}
        type="button"
        title="More options"
      >
        ⋮
      </button>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <button
              style={styles.menuIconBtn}
              onClick={handlePrint}
              title="Print"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
            <button
              style={styles.menuIconBtn}
              onClick={handleDownload}
              title="Download"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function makeBarScale(maxVal) {
  if (!maxVal || maxVal <= 0) return { max: 5, step: 1 };
  const raw = maxVal / 5;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  let niceNorm;
  if (norm <= 1) niceNorm = 1;
  else if (norm <= 2) niceNorm = 2;
  else if (norm <= 2.5) niceNorm = 2.5;
  else if (norm <= 5) niceNorm = 5;
  else niceNorm = 10;
  const step = niceNorm * mag;
  let max = Math.ceil(maxVal / step) * step;
  if (max - maxVal < step * 0.15) max += step;
  return { max, step };
}

function AgingSegmentDonutCard({ rows, onDragStart, onDragEnd }) {
  const chartRef = useRef();
  const [byType, setByType] = useState("Amount");
  const [topN, setTopN] = useState("10");

  const entries = useMemo(() => {
    const n = parseInt(topN, 10) || 10;
    return sortAgeRowsAsc(rows)
      .map((r) => ({
        label: r.age || "Other",
        value: byType === "Amount" ? Number(r.salesAmount) || 0 : Number(r.qty) || 0,
      }))
      .slice(0, n);
  }, [rows, byType, topN]);

  const total = entries.reduce((s, e) => s + e.value, 0);
  const data = {
    labels: entries.map((e) => e.label),
    datasets: [
      {
        data: entries.map((e) => e.value),
        backgroundColor: [
          "#5CC7C1",
          "#5C74C7",
          "#52ABE1",
          "#B2B3F5",
          "#F06547",
          "#3CC3DF",
          "#434549",
          "#FB83B3",
          "#6FD195",
          "#F4CF3B",
        ],
        borderColor: "#FFFFFF",
        borderWidth: 1,
        cutout: "68%",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          padding: 12,
          color: "rgba(0, 0, 0, 0.7)",
          font: { family: "Segoe UI", size: 12, weight: "400" },
        },
      },
      tooltip: {
        callbacks: {
          label(ctx) {
            const v = Number(ctx.raw || 0);
            const pct = total > 0 ? ((v / total) * 100).toFixed(2) : "0";
            return ` ${ctx.label}: ${formatNum(v)} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Aging Segment</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByAmountsDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <div style={styles.donutWrap}>
          <Doughnut ref={chartRef} data={data} options={options} />
        </div>
      </div>
    </div>
  );
}

function IndividualCompareBarCard({ rows, onDragStart, onDragEnd }) {
  const chartRef = useRef();
  const [byType, setByType] = useState("Amount");
  const [topN, setTopN] = useState("10");
  const sorted = useMemo(() => {
    const n = parseInt(topN, 10) || 10;
    const list = sortAgeRowsAsc(rows);
    return list.slice(0, n);
  }, [rows, byType, topN]);

  const labels = sorted.map((r) => r.age || "—");
  const targetData = sorted.map((r) => Number(r.qty) || 0);
  const salesData = sorted.map((r) => Number(r.salesAmount) || 0);

  const qtyMax = Math.max(...targetData, 0);
  const salesMax = Math.max(...salesData, 0);
  const qtyScale = makeBarScale(qtyMax);
  const salesScale = makeBarScale(salesMax);

  const data = {
    labels,
    datasets: [
      {
        label: "Qty",
        data: targetData,
        backgroundColor: "#5CC7C1",
        borderRadius: 0,
        barThickness: 24,
        yAxisID: "yQty",
      },
      {
        label: "Sales",
        data: salesData,
        backgroundColor: "#5C74C7",
        borderRadius: 0,
        barThickness: 24,
        yAxisID: "ySales",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rectRounded",
          boxWidth: 8,
          boxHeight: 8,
          padding: 12,
          color: "rgba(0, 0, 0, 0.7)",
          font: { family: "Segoe UI", size: 12, weight: "400" },
        },
      },
      tooltip: {
        callbacks: {
          label(ctx) {
            const v = Number(ctx.raw) || 0;
            if (ctx.dataset.label === "Sales") return ` Sales: ${formatNum(v)}`;
            return ` Qty: ${formatNum(v)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          color: "#6b7280",
          maxRotation: 90,
          minRotation: 90,
          font: { size: 12 },
        },
      },
      yQty: {
        position: "left",
        beginAtZero: true,
        max: qtyScale.max,
        ticks: {
          stepSize: qtyScale.step,
          color: "#6b7280",
          font: { size: 12 },
          callback: (v) => formatNum(v),
        },
        title: {
          display: true,
          text: "Qty",
          color: "#6b7280",
          font: { size: 12 },
        },
        grid: { color: "rgba(0,0,0,0.08)", drawBorder: false },
      },
      ySales: {
        position: "right",
        beginAtZero: true,
        max: salesScale.max,
        ticks: {
          stepSize: salesScale.step,
          color: "#6b7280",
          font: { size: 12 },
          callback: (v) => formatNum(v),
        },
        title: {
          display: true,
          text: "Sales",
          color: "#6b7280",
          font: { size: 12 },
        },
        grid: { drawOnChartArea: false, drawBorder: false },
      },
    },
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Item Comparing sales and qty</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByAmountsDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Bar ref={chartRef} data={data} options={options} />
      </div>
    </div>
  );
}


export default function StockAgingChart({ rows = [] }) {
  const safeRows = useMemo(() => rows.filter(Boolean), [rows]);
  const [chartOrder, setChartOrder] = useState([
    "agingSegment",
    "itemSales",
  ]);
  const [draggingChart, setDraggingChart] = useState(null);

  const renderChart = {
    agingSegment: (id) => (
       <AgingSegmentDonutCard
        rows={safeRows}
        onDragStart={() => setDraggingChart(id)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
    itemSales: (id) => (
      <IndividualCompareBarCard
        rows={safeRows}
        onDragStart={() => setDraggingChart(id)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
  };

  function handleDrop(targetId) {
    if (!draggingChart || draggingChart === targetId) return;
    const next = [...chartOrder];
    const fromIdx = next.indexOf(draggingChart);
    const toIdx = next.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setChartOrder(next);
    setDraggingChart(null);
  }

  return (
    <div style={styles.root}>
      <div style={styles.grid}>
        {chartOrder.map((cardId) => (
          <div
            key={cardId}
            style={styles.colItem}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(cardId)}
          >
            {renderChart[cardId](cardId)}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  root: { padding: "0 20px 16px" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },
  colItem: { minWidth: 0, position: "relative" },
  dragHandle: {
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: 4,
    lineHeight: 0,
    background: "transparent",
    border: "none",
    padding: 0,
  },
  card: {
    boxSizing: "border-box",
    fontFamily: 'Segoe UI',
    background: "#FFFFFF",
    border: "1px solid #E3E3E3",
    borderRadius: 8,
    padding: "24px 16px",
    height: "100%",
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    padding: "0 0 24px",
    marginBottom: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
    marginLeft: "auto",
  },
  sectionTitle: {
    fontFamily: 'Segoe UI',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "24px",
    color: "#606060",
    margin: 0,
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  inlineTitle: {
    fontFamily: 'Segoe UI',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "24px",
    color: "#606060",
    whiteSpace: "nowrap",
  },
  chartWrap: { height: 420, position: "relative" },
  donutWrap: {
    minHeight: 420,
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 10,
    boxSizing: "border-box",
  },
  tableWrap: {
    maxHeight: 320,
    overflow: "auto",
    borderTop: "1px solid #eee",
  },
  dataTable: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontSize: 11,
  },
  th: {
    textAlign: "left",
    padding: "8px 6px",
    borderBottom: "1px solid #eee",
    color: "#6b7280",
    fontWeight: 600,
  },
  thIndex: {
    width: 36,
    textAlign: "center",
    padding: "8px 4px",
    borderBottom: "1px solid #eee",
    color: "#6b7280",
    fontWeight: 600,
  },
  tdIndex: {
    textAlign: "center",
    padding: "8px 4px",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
  },
  tdName: {
    textAlign: "left",
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    color: "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  tdNum: {
    textAlign: "right",
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
  },
  topNWrapper: { position: "relative" },
  topNBtn: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#FFFFFF",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: 'Segoe UI',
    lineHeight: "normal",
    outline: "none",
  },
  topNChevron: { fontSize: 14, color: "#606060" },
  topNMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
  },
  topNOption: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 14,
    cursor: "pointer",
    color: "#606060",
    gap: 4,
    border: "none",
    width: "100%",
    textAlign: "left",
    background: "transparent",
  },
  byTypeWrapper: { position: "relative" },
  byTypeBtn: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#FFFFFF",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: 'Segoe UI',
    lineHeight: "normal",
    outline: "none",
  },
  byTypeChevron: { fontSize: 14, color: "#606060" },
  byTypeMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
  },
  byTypeOption: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 14,
    cursor: "pointer",
    color: "#606060",
    gap: 4,
    border: "none",
    width: "100%",
    textAlign: "left",
    background: "transparent",
  },
  menuWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dots: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#606060",
    fontSize: 22,
    lineHeight: 1,
    padding: 0,
    userSelect: "none",
    outline: "none",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 12px)",
    background: "#fff",
    border: "1px solid #e9e9e9",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.12)",
    padding: "8px 12px",
    zIndex: 50,
    minWidth: 110,
  },
  menuIconRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  menuIconBtn: {
    width: 34,
    height: 34,
    border: "none",
    background: "transparent",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#666",
    padding: 0,
  },
};
